import { Button, Datagrid, FilterForm, FunctionField, Pagination, SelectField, SelectInput, Show, SimpleShowLayout, TextField } from "ra-ui-materialui";
import { useParams } from "react-router";
import ListActions from "../common/ListActions";
import {
  ActionTitles,
  MATCH_EVENT_TYPES_CHOICES,
  MATCH_EVENT_TYPES_ICON_CHOICES,
  RESOURCES,
  RESOURCES_BASEPATH,
  SCREENING_MATCH_STATUS_ICON,
  SCREENING_REPORT_TYPES_DISPLAY_NAME,
  SUB_RESOURCES
} from "../Constants";
import { contentCenter, dataGrid, float, iconAction, listRoot, padding0, padding1, textColor } from "../Styles";
import { Divider, Grid, List, ListItem } from "@mui/material";
import { IDListItem } from "./IdentityRequestShow";
import { ListContextProvider, useDataProvider, useList, useNotify, useRecordContext, useRefresh } from "ra-core";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import { cloneDeep, defaultsDeep, isEmpty } from "lodash";
import { useState } from "react";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import CustomTooltip from "../../utils/CustomTooltip";
import DisableMonitoringButton from "./DisableMonitoringButton";
import { isAdminUser } from "../DataProvider";
import { ScreeningEventDialog } from "./ScreeningEventDialog";
import DownloadScreeningEvent from "./DownloadScreeningEvent";
import { DaysToRenewalField } from "./DaysToRenewalField";

export const ViewScreeningReport = () => {
  const { requestId, reportId, clientId } = useParams();  
  const isAdmin = isAdminUser();
  
  return (
    <Show
      actions={
        <ListActions
          title={ActionTitles.IDENTITY_VERIFICATION_SCREENING_REPORT}
          showViewButton={true}
          showDeleteButton={false}
          resource={RESOURCES.IDENTITY_VERIFICATION}
          basePath={RESOURCES_BASEPATH.IDENTITY_VERIFICATION}
          record={isAdmin ? { id : `client/${clientId}/identity-verification/${requestId}`} : { id: requestId }}          
        />
      }
      id={reportId}
      renderButtonAndSearch={false}
      sx={contentCenter}
      resource={SUB_RESOURCES.SCREENING_REPORT}
      queryOptions={ isAdmin ? { meta : { clientId, requestId }} : { meta: { requestId }}}
    >
      <SimpleShowLayout>
        <ScreeningReportShowGrid id={requestId} reportId={reportId} isAdmin={isAdmin} />
      </SimpleShowLayout>
    </Show>
  );
}

export const ScreeningReportShowGrid = (props) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { id, reportId, isAdmin } = props;
  const [pauseCMLoading, setPauseCMLoading] = useState(false);
  
  const pauseContinuousMonitoring = () => {
    setPauseCMLoading(true);
    dataProvider
    .pauseContinuousMonitoring({ id, reportId })
    .then(() => {      
      notify('Continuous monitoring disabled', "info", { autoHideDuration: 1000 });
      refresh();
    })
    .catch((error) => {
      notify(error?.body ? error.body : 'Something went wrong while disabling continuous monitoring', "warning", { autoHideDuration: 1000 });
    }).finally(() => {
      setPauseCMLoading(false);
    });
  }

  return(    
    <List sx={{...padding0.y, width : "100%"}} >            
      <ListItem sx={{ padding : '0px' }}>
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <IDListItem
              title="Report type"
              value={SCREENING_REPORT_TYPES_DISPLAY_NAME[record["reportType"]]}
            />
          </Grid>
          <Grid item xs={6}>
            <IDListItem
              title="Continuous Monitoring"
              value={
                record["continuousMonitoringEnabled"] ? (
                  <CheckCircleIcon sx={{ ...textColor.green, ...float.left }} />
                ) : (
                  <CancelIcon sx={{ ...textColor.red, ...float.left }} />
                )
              }
            />
          </Grid>
        </Grid>
      </ListItem>
      <Divider />
      <ListItem sx={{ padding : '0px' }} >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6}>
            <IDListItem
              title="Match Status"
              value={SCREENING_MATCH_STATUS_ICON[record["screeningMatchStatus"]]}
            />
          </Grid>
          <Grid item xs={6}>
            <IDListItem
              title="Days To Renewal"
              value={<DaysToRenewalField 
                      days={record["daysToRenewal"]}
                      sx={{
                        height: '30px',
                        width: '35px'
                      }}
                    />}
            />
          </Grid>
        </Grid>
      </ListItem>
      <Divider />      
      <ListItem sx={{ padding : '0px' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6}>
            <IDListItem title="Created" value={record["created"]} />
          </Grid>
          <Grid item xs={6}>
            <IDListItem title="Monitoring Started" value={record["cmStartedAt"]} />
          </Grid>
        </Grid>
      </ListItem>
      <Divider />      
      <ListItem sx={{ padding : '0px' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6}>
            <IDListItem title="Last Modified" value={record["lastModified"]} />
          </Grid>
          <Grid item xs={6}>
            <IDListItem title="Monitoring End" value={record["cmFinishedAt"]} />
          </Grid>
        </Grid>
      </ListItem>
      {!isEmpty(record['events']) && <EventList data={record['events']} id={id} reportId={reportId} />}          
      {!isAdmin && 
      <ListItem sx={padding1.y}>
        <Grid container justifyContent="flex-end">          
          <Grid container item xs={12} lg={10} justifyContent="flex-end">              
            <Grid item xs={12} md={4} lg={3} container>
              <DisableMonitoringButton 
                buttonLabel="DISABLE MONITORING"
                dialogTitle="Disable Continuous Monitoring"
                dialogContent={<p>Are you sure you want to disable continuous monitoring for this report?</p>}
                onConfirm={pauseContinuousMonitoring}
                disableSubmitButton={pauseCMLoading}
                loading={pauseCMLoading}
                disableButton={!record["continuousMonitoringEnabled"]}              
              />
            </Grid>              
          </Grid>
        </Grid>
      </ListItem>}
    </List>
  );
};

export const EventList = ({ data, id, reportId }) => {    
  const { clientId } = useParams();
  const listContext = useList({ 
    data, 
    perPage: 5, 
    page: 1, 
    sort: { field: 'lastModified', order: 'DESC' }
  });
  
  const [open, setOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();

  const handleDialogClose = () => {      
    setOpen(false);
  };  

  const filters = [
    <SelectInput
      id="screening-report-event-status-filter"
      source="q"
      alwaysOn
      label="Status"
      size="small"
      variant="outlined"
      choices={MATCH_EVENT_TYPES_CHOICES}    
    />
];

  return(
    <ListContextProvider value={listContext}>
      <Grid>
        <Grid item xs={12}>
          <FilterForm filters={filters} />
        </Grid>        
        <Datagrid        
          sx={{
            ...defaultsDeep(
              cloneDeep(dataGrid.base),
              cloneDeep(dataGrid.firstColSticky)
            ),
            ...listRoot["& .RaList-content"]
          }}
          bulkActionButtons={false}
          empty={null}
        >          
          <TextField source="name" />
          <TextField source="birthRecord" />          
          <SelectField source="status" choices={MATCH_EVENT_TYPES_ICON_CHOICES} />
          <TextField source="lastModified" />          
          <TextField source="reviewedBy" />
          <TextField source="reviewedAt" />
          <FunctionField
            source="View"
            label={false}
            sortable={false}
            render={(record) => {
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <DownloadScreeningEvent id={id} eventId={record.id} reportId={reportId} userName={record?.name} showOnlyIcon={true} clientId={clientId}></DownloadScreeningEvent>
                  <CustomTooltip
                    title="View"
                    placement="top"
                  >
                    <Button
                      id={`screening-report-view-event-${record.id}`}
                      sx={iconAction}
                      size="medium"
                      onClick={() => {
                        setOpen(true);
                        setSelectedRecord(record);
                      }}
                    >
                      <VisibilityIcon />
                    </Button>
                  </CustomTooltip>

                </div>
              );
            }}
          />                    
        </Datagrid>
        <ScreeningEventDialog 
          openDialog={open}
          closeDialog={handleDialogClose}
          event={selectedRecord}
          id={id}
          reportId={reportId}
        />
        <Grid sx={dataGrid.pagination}>
          <Pagination sx={float.left} />
        </Grid>
      </Grid>      
    </ListContextProvider>
  );
};